<template>
  <nav aria-label="Breadcrumb">
    <ol role="list" class="flex flex-wrap items-center text-text-md px-4 py-2 bg-gray-100 rounded-sm">
      <li
        v-for="(item, index) in items" :key="`${item.label}_${index}`" class="flex items-center"
        :class="{ 'fs-exclude': item.fsExclude }"
        :aria-current="isCurrentPage(item, index) ? 'page' : false"
        role="listitem"
      >
        <a
          v-if="item.href"
          :href="item.href"
          class="text-blue-400 hover:text-blue-600 flex items-center"
        >
          <span v-if="item.icon" class="material-icons-round mr-1 text-base" aria-hidden="true">{{ item.icon }}</span>
          <span class="hover:underline">{{ item.label }}</span>
        </a>
        <router-link
          v-else-if="item.to && !isActive(item)"
          :to="item.to"
          @click="handleClick(item)"
          class="text-blue-400 hover:text-blue-600 flex items-center"
        >
          <span v-if="item.icon" class="material-icons-round mr-1 text-base" aria-hidden="true">{{ item.icon }}</span>
          <span class="hover:underline">{{ item.label }}</span>
        </router-link>
        <span
          v-else
          :class="{ 'text-gray-900': !item.to || isActive(item) }"
          class="flex items-center"
        >
          <span v-if="item.icon" class="material-icons-round mr-1 text-base" aria-hidden="true">{{ item.icon }}</span>
          {{ item.label }}
        </span>
        <span v-if="!isLastItem(index)" aria-hidden="true" class="px-2 text-gray-800">/</span>
      </li>
    </ol>
  </nav>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'

export interface BreadcrumbItem {
  label: string
  to?: {
    name: string
    params?: Record<string, string | string[]>
    query?: Record<string, string | string[]>
  } | string
  /** A page outside this app's router, which a router link cannot reach. */
  href?: string
  icon?: string
  /** Marks the current page when several crumbs share a route name and differ only by params. */
  isCurrent?: boolean
  /** Keeps a crumb that names private data, such as a student folder, out of FullStory. */
  fsExclude?: boolean
}

const props = defineProps<{
  items: BreadcrumbItem[]
}>()

const route = useRoute()

const isLastItem = (index: number) => {
  return index === props.items.length - 1
}

const isActive = (item: BreadcrumbItem) => {
  if (item.isCurrent !== undefined) return item.isCurrent
  if (!item.to) return false

  if (typeof item.to === 'string') {
    return route.path === item.to
  }

  return item.to.name === route.name
}

// The last crumb with nowhere to link is the page itself, which no route comparison can identify.
const isCurrentPage = (item: BreadcrumbItem, index: number) =>
  isActive(item) || (isLastItem(index) && !item.to && !item.href)

const emit = defineEmits<{
  (e: 'click', item: BreadcrumbItem): void
}>()

const handleClick = (item: BreadcrumbItem) => {
  emit('click', item)
}
</script>

